import { useSyncExternalStore } from 'react';
import apiService from './compat/api';

/**
 * ONE PALETTE FOR THE FOUR DISPOSITIONS, TAKEN FROM THE DISPOSITION MATRIX.
 *
 * Owner-directed. Before this there were THREE hardcoded palettes for the same four
 * outcomes, and none of them was the one an administrator can actually edit:
 *
 *   apr.scoring_thresholds (the Disposition Matrix screen)  #dc3545 #fd7e14 #ffc107 #28a745
 *   getRecommendationColor (Initial Portfolio Scoring)      #dc2626 #f59e0b #3b82f6 #10b981
 *   dispositionInk (the row chips)                          #7f1d1d #9a3412 #1e3a8a #14532d
 *
 * So Re-Design was amber in the matrix and BLUE on the scoring screen — the same word, the
 * same application, two different colours depending which screen you were looking at. And
 * editing the matrix changed neither of the other two, which makes the colour picker on that
 * screen a lie.
 *
 * The matrix is now the source. `color` on each threshold row is the truth, and everything
 * else is derived from it.
 *
 * ── WHY THE STORED COLOUR IS NOT USED DIRECTLY FOR TEXT ────────────────────────────────
 *
 * Those values are FILL colours, chosen to sit behind white text. Re-Design's #ffc107 on
 * white is roughly 1.6:1 — illegible, and no amount of good intent changes that. So each role
 * is derived from the same hue rather than picked separately:
 *
 *   base    the stored colour, unchanged — fills, bars, the dot on a legend
 *   ink     the same hue darkened until it passes contrast on white — text and 1px rules
 *   tint    the same hue at ~14% — chip and row backgrounds
 *
 * Derivation means an administrator changing a colour in the matrix moves the text and the
 * tint with it, in step, and cannot produce an unreadable screen by choosing a pale yellow.
 */

export interface DispositionColor {
  /** The matrix's own value, unchanged. Chip fills, bars. */
  base: string;
  /** Text drawn ON `base` — black or white, whichever is readable. */
  onBase: string;
  /** `base` darkened until it is legible on white. Text and 1px rules. */
  ink: string;
  /** A wash of the same hue, for row and panel backgrounds. */
  tint: string;
}

/** Used until the matrix loads, and for anything the matrix does not name. */
const NEUTRAL: DispositionColor = {
  base: '#6c757d',
  onBase: '#ffffff',
  ink: '#3c4043',
  tint: 'rgba(60,64,67,0.10)',
};

/**
 * The matrix values as shipped, so the first paint is right rather than grey.
 * Replaced by whatever the install actually has as soon as the request returns.
 */
const SEED: Record<string, string> = {
  Retire: '#dc3545',
  'Re-Platform': '#fd7e14',
  'Re-Design': '#ffc107',
  Retain: '#28a745',
};

function parse(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1]!, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** WCAG relative luminance — the same formula the contrast ratio is defined by. */
function luminance([r, g, b]: [number, number, number]): number {
  const f = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

const contrastOnWhite = (rgb: [number, number, number]) => 1.05 / (luminance(rgb) + 0.05);

/**
 * Darken toward black until the colour clears 4.5:1 on white.
 *
 * Multiplicative, so the HUE is preserved — a red stays red and an amber stays amber, it
 * simply gets deep enough to read. Stops at the first step that passes rather than going as
 * dark as possible, which keeps the family visibly related to the fills.
 */
function inkFor(rgb: [number, number, number]): string {
  let scale = 1;
  let current = rgb;
  while (contrastOnWhite(current) < 4.5 && scale > 0.15) {
    scale -= 0.05;
    current = [rgb[0] * scale, rgb[1] * scale, rgb[2] * scale] as [number, number, number];
  }
  const hex = current.map((v) => Math.round(v).toString(16).padStart(2, '0')).join('');
  return `#${hex}`;
}

/**
 * Black or white on this fill, whichever is readable.
 *
 * The matrix holds both #ffc107 and #dc3545 — one needs dark text, the other light. Picking
 * per colour by eye is how a chip becomes unreadable the day somebody edits the matrix, so it
 * is computed. The dark option is a deep brown rather than pure black: on a saturated amber,
 * black reads as a hole and a dark tint of the hue keeps the chip looking like one object.
 */
function readableOn(rgb: [number, number, number]): string {
  return luminance(rgb) > 0.42 ? '#3d2f00' : '#ffffff';
}

function toColor(hex: string): DispositionColor {
  const rgb = parse(hex);
  if (!rgb) return NEUTRAL;
  return {
    base: hex.startsWith('#') ? hex : `#${hex}`,
    onBase: readableOn(rgb),
    ink: inkFor(rgb),
    tint: `rgba(${Math.round(rgb[0])}, ${Math.round(rgb[1])}, ${Math.round(rgb[2])}, 0.14)`,
  };
}

export type DispositionPalette = (recommendation: string | null | undefined) => DispositionColor;

function build(byName: Record<string, string>): DispositionPalette {
  const cache = new Map<string, DispositionColor>();
  return (recommendation) => {
    const key = (recommendation ?? '').trim();
    if (!key) return NEUTRAL;
    const stored = byName[key];
    if (!stored) return NEUTRAL;
    let hit = cache.get(key);
    if (!hit) {
      hit = toColor(stored);
      cache.set(key, hit);
    }
    return hit;
  };
}

/**
 * ONE STORE, ONE REQUEST.
 *
 * A hook that fetched per component would fire once per ROW — five hundred requests to read
 * four colours, on a screen whose whole job is to render a long table. The matrix is install
 * configuration: it is the same for everybody and changes when an administrator edits it, so
 * it is fetched once for the tab and shared.
 *
 * `useSyncExternalStore` rather than a context, so a component can read the palette without
 * every screen having to mount a provider — and so the first paint uses the shipped values
 * instead of flashing grey while a request is in flight.
 */
let current: DispositionPalette = build(SEED);
let loaded = false;
const listeners = new Set<() => void>();

function loadOnce(): void {
  if (loaded) return;
  loaded = true;
  void apiService
    .getScoringThresholds()
    .then((rows: unknown) => {
      if (!Array.isArray(rows)) return;
      const next: Record<string, string> = {};
      // `getScoringThresholds` maps each row to `{ name, color, … }` (compat/api.ts) — it never
      // carries `decision_name`. Reading only `decision_name` meant this loop matched nothing,
      // `next` stayed empty, and every screen kept the shipped SEED colours: editing the
      // Disposition Matrix changed no screen. Accept either shape. (Found 2026-10-19.)
      for (const row of rows as { decision_name?: string; name?: string; color?: string }[]) {
        const key = row?.decision_name ?? row?.name;
        if (key && row?.color) next[key] = row.color;
      }
      if (Object.keys(next).length === 0) return;
      current = build(next);
      for (const notify of listeners) notify();
    })
    .catch(() => {
      /* keep the shipped values — a colour scheme is not worth an error state */
    });
}

function subscribe(listener: () => void): () => void {
  loadOnce();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The palette for this install. Safe to call from any number of rows. */
export function useDispositionPalette(): DispositionPalette {
  return useSyncExternalStore(
    subscribe,
    () => current,
    () => current,
  );
}

/**
 * The same lookup outside React — for a helper that colours a value without rendering.
 * Returns the shipped palette until the matrix has loaded.
 */
export function dispositionColor(recommendation: string | null | undefined): DispositionColor {
  loadOnce();
  return current(recommendation);
}
